import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, Download, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fileId = (url = "") => (url.match(/\/api\/files\/([a-f0-9]{24})/i) || [])[1];
const abs = (url = "") => (url.startsWith("/") ? `${API}${url}` : url);

// Visor PDF a pantalla completa dentro del portafolio (sin salir de la página).
export const PdfViewer = ({ url, title, onClose }) => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const fid = fileId(url);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!fid) return setError(true);
    fetch(`${API}/api/pdf/${fid}/info`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setInfo)
      .catch(() => setError(true));
  }, [fid]);

  useEffect(() => {
    // El botón "atrás" del celular cierra el visor en vez de salir del portafolio.
    window.history.pushState({ pdfViewer: true }, "");
    const onPop = () => closeRef.current();
    window.addEventListener("popstate", onPop);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("popstate", onPop);
      document.body.style.overflow = prev;
    };
  }, []);

  const close = () => window.history.back();

  return createPortal(
    <div className="fixed inset-0 z-[200] flex flex-col bg-[#0b0f1a]" data-testid="pdf-viewer">
      <div className="safe-top shrink-0 border-b border-white/10 bg-black/70 backdrop-blur">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          onClick={close}
          className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-slate-900 shadow active:scale-95"
          data-testid="pdf-viewer-back"
        >
          <ArrowLeft className="h-4 w-4" /> Regresar
        </button>
        <p className="min-w-0 flex-1 truncate text-center text-sm font-medium text-white/80">{title}</p>
        <a
          href={abs(url)}
          download={`${title || "documento"}.pdf`}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white active:scale-95"
          data-testid="pdf-viewer-download"
          aria-label="Descargar PDF"
        >
          <Download className="h-4 w-4" />
        </a>
      </div>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain px-2 py-3" style={{ WebkitOverflowScrolling: "touch" }}>
        {error ? (
          <p className="mt-20 text-center text-sm text-white/70" data-testid="pdf-viewer-error">
            No se pudo abrir el PDF.
          </p>
        ) : !info ? (
          <div className="mt-20 flex justify-center text-white/70">
            <Loader2 className="h-7 w-7 animate-spin" />
          </div>
        ) : (
          <div className="mx-auto flex max-w-3xl flex-col gap-3">
            {info.sizes.map((s, i) => (
              <img
                key={i}
                src={`${API}/api/pdf/${fid}/page/${i + 1}`}
                alt={`Página ${i + 1}`}
                loading={i < 2 ? "eager" : "lazy"}
                className="w-full rounded-md bg-white shadow-lg"
                style={{ aspectRatio: `${s.w} / ${s.h}` }}
                data-testid={`pdf-page-${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
